import { getConsent } from 'features/consent/consentStore';

// GA4 Measurement ID — verify this matches the `G-…` ID in your GA4 property
// (Data Streams → Web → Measurement ID). Update here and in public/index.html.
const MEASUREMENT_ID = 'G-JW0W4605MC';

// GA4 custom dimensions must ride on each event's params. gtag('set', {...})
// silently drops arbitrary custom keys (verified empirically against the live
// tag), so merging here — the single choke point every track* function already
// uses — is what actually delivers content_locale/partner to GA4.
let contextParams = {};

function gtag(...args) {
  if (typeof window.gtag !== 'function') return;
  // window.gtag is now ALWAYS defined — public/index.html installs a dataLayer
  // stub so the Consent Mode default can be declared before anything else runs.
  // That makes the function check insufficient on its own: without this consent
  // gate, events fired before a visitor decides would sit in the dataLayer queue
  // and then all flush to Google the instant the tag loads on "Accept". Nothing
  // is measured until analytics consent actually exists.
  if (!getConsent().analytics) return;
  if (args[0] === 'event') {
    const [, eventName, params] = args;
    window.gtag('event', eventName, { ...contextParams, ...(params || {}) });
    return;
  }
  window.gtag(...args);
}

// ─── Page views (SPA) ────────────────────────────────────────────────────────
// Event: page_view. The gtag('config') in index.html fires the FIRST page_view
// on load; this sends one on every client-side route change so single-page
// navigations (e.g. clicking a nav link) show up without relying on GA4's
// Enhanced Measurement "page changes" toggle being enabled.
export function trackPageView({ page_path, page_title }) {
  gtag('event', 'page_view', {
    page_path: String(page_path).slice(0, 200),
    page_title: String(page_title).slice(0, 200),
    page_location: window.location.href,
  });
}

// ─── Navigation ────────────────────────────────────────────────────────────
// Event: navigation_click
// Fired when any nav link (desktop or mobile) is clicked.
// Params:
//   nav_label       — visible text of the clicked link (e.g. "Trendline IQ")
//   nav_destination — href of the link (e.g. "/trendline-iq")
//   nav_type        — "desktop_nav" | "mobile_nav"
export function trackNavClick({ label, destination, nav_type = 'desktop_nav' }) {
  gtag('event', 'navigation_click', {
    nav_label: String(label).slice(0, 100),
    nav_destination: String(destination).slice(0, 100),
    nav_type,
  });
}

// ─── Library filters ───────────────────────────────────────────────────────
// Event: filter_applied
// Fired when a filter facet chip is toggled on.
// Params:
//   filter_group  — facet group key (e.g. "categories", "platform", "style")
//   filter_value  — the facet ID that was toggled (e.g. "market-iq")
//   result_count  — visible product count after the filter takes effect
export function trackFilterApplied({ filter_group, filter_value, result_count }) {
  gtag('event', 'filter_applied', {
    filter_group: String(filter_group).slice(0, 40),
    filter_value: String(filter_value).slice(0, 100),
    result_count: Number(result_count),
  });
}

// Event: filter_cleared
// Fired when an entire facet group is cleared (e.g. "Clear" next to a group).
// Params:
//   filter_group — the facet group that was cleared
export function trackFilterCleared({ filter_group }) {
  gtag('event', 'filter_cleared', {
    filter_group: String(filter_group).slice(0, 40),
  });
}

// Event: filter_reset
// Fired when the "Reset Filters" button clears all active facets at once.
export function trackFilterReset() {
  gtag('event', 'filter_reset');
}

// ─── Copy to clipboard ─────────────────────────────────────────────────────
// Event: copy_code
// Fired when a user copies text from a <pre> or <code> element.
// Params:
//   content_snippet — first 120 characters of the copied text (no PII trimming needed
//                     for code blocks, but kept short to stay within GA4 limits)
export function trackCopyCode({ content_snippet = '' }) {
  gtag('event', 'copy_code', {
    content_snippet: String(content_snippet).slice(0, 120),
  });
}

// ─── Scroll depth ──────────────────────────────────────────────────────────
// Event: scroll_depth_reached
// Fired once per page load at each threshold (25, 50, 75, 100).
// GA4's built-in scroll event only fires at 90% — this gives finer breakpoints.
// Params:
//   percent_scrolled — 25 | 50 | 75 | 100
//   page_path        — window.location.pathname at time of fire
//   page_title       — document.title at time of fire
export function trackScrollDepth({ percent_scrolled, page_path, page_title }) {
  gtag('event', 'scroll_depth_reached', {
    percent_scrolled: Number(percent_scrolled),
    page_path: String(page_path).slice(0, 100),
    page_title: String(page_title).slice(0, 100),
  });
}

// ─── Generic event bus (data-ti-track) ───────────────────────────────────────
// Fired by useTiClickTracking for any element tagged `data-ti-track="event_name"`.
// Every `data-ti-*` attribute on that element (except data-ti-track) becomes a
// param, kebab → snake case. Lets us add a checkpoint anywhere with just markup.
export function trackEvent(name, params = {}) {
  if (!name) return;
  gtag('event', String(name).slice(0, 40), params);
}

// Parse a display price like "$597" / "$1,299.00" into a number (597 / 1299).
// Returns undefined when there's no parseable number so GA omits the field
// rather than sending NaN.
function parsePrice(v) {
  if (v == null) return undefined;
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : undefined;
}

// ─── Ecommerce: product view ─────────────────────────────────────────────────
// Event: view_item (GA4 recommended). Fired when a product PDP mounts.
// `currency` is an ISO-4217 code carried by the page data. It defaults to USD,
// which is what every SKU but the Finance 2026 EUR offer is priced in — an
// omitted currency must keep reporting exactly as it did before.
export function trackViewItem({ item_id, item_name, price, currency = 'USD' }) {
  const value = parsePrice(price);
  gtag('event', 'view_item', {
    currency,
    ...(value != null ? { value } : {}),
    items: [{
      item_id: String(item_id || '').slice(0, 100),
      item_name: String(item_name || '').slice(0, 100),
      ...(value != null ? { price: value } : {}),
    }],
  });
}

// ─── Ecommerce: begin checkout (the Spiffy handoff) ──────────────────────────
// Event: begin_checkout (GA4 recommended). Fired the moment a user clicks any
// checkout link (secure.tradingindicators.com/checkout/*), right before they
// leave for the external payment handler. This is the last event we can see on
// our side, so it's the conversion proxy for the funnel.
//   item_id     — product slug parsed from the checkout URL
//   item_name   — product name (from data-ti-item, falls back to slug)
//   variant     — plan/variant label (e.g. "+Coaching", "MT5") if provided
//   value       — numeric price (from data-ti-value) if provided
//   currency    — ISO-4217 code (from data-ti-currency), defaults to USD
//   destination — full checkout URL
export function trackBeginCheckout({ item_id, item_name, variant, value, destination, currency = 'USD' }) {
  const v = parsePrice(value);
  gtag('event', 'begin_checkout', {
    currency,
    ...(v != null ? { value: v } : {}),
    ...(destination ? { destination: String(destination).slice(0, 200) } : {}),
    items: [{
      item_id: String(item_id || '').slice(0, 100),
      item_name: String(item_name || item_id || '').slice(0, 100),
      ...(variant ? { item_variant: String(variant).slice(0, 100) } : {}),
      ...(v != null ? { price: v } : {}),
    }],
  });
}

// ─── Outbound links ──────────────────────────────────────────────────────────
// Event: outbound_click. Fired for clicks on links to a different host (Scanner,
// Discord, etc.). Checkout links are handled by begin_checkout instead.
export function trackOutboundClick({ destination, label }) {
  gtag('event', 'outbound_click', {
    destination: String(destination || '').slice(0, 200),
    link_label: String(label || '').slice(0, 100),
  });
}

// ─── FAQ engagement ──────────────────────────────────────────────────────────
// Event: faq_open. Fired when a FAQ accordion item is expanded (not on close).
export function trackFaqOpen({ question, page_path }) {
  gtag('event', 'faq_open', {
    question: String(question || '').slice(0, 150),
    page_path: String(page_path || '').slice(0, 100),
  });
}

// ─── Language ──────────────────────────────────────────────────────────────
// Event: language_switch
// Fired when a visitor uses the header language control.
// Params:
//   from — locale being left (e.g. "en")
//   to   — locale being entered (e.g. "de")
export function trackLanguageSwitch({ from, to }) {
  gtag('event', 'language_switch', {
    from: String(from).slice(0, 10),
    to: String(to).slice(0, 10),
  });
}

// ─── Measurement context ───────────────────────────────────────────────────
// content_locale and partner are attached to EVERY event by merging them into
// each event's params inside the gtag() wrapper above, so no existing tracking
// call site needs to change. Both must also be registered as event-scoped
// custom dimensions in GA4 Admin — see docs; code alone will not surface them
// in reports, and they only populate from registration day forward.
const PARTNER_STORAGE_KEY = 'ti_partner';

export function getPartner() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('partner');
    if (fromUrl) {
      const clean = fromUrl.slice(0, 40);
      window.sessionStorage.setItem(PARTNER_STORAGE_KEY, clean);
      return clean;
    }
    return window.sessionStorage.getItem(PARTNER_STORAGE_KEY) || undefined;
  } catch (_e) {
    return undefined;
  }
}

export function initAnalyticsContext({ locale }) {
  const partner = getPartner();
  contextParams = {
    content_locale: String(locale || 'en').slice(0, 10),
    ...(partner ? { partner } : {}),
  };
}

export { MEASUREMENT_ID };
